/** Client-side limits aligned with backend env (CONTENT_*) — use VITE_ prefix in .env */

export type MetaAspectRatio = "1:1" | "4:5" | "9:16" | "16:9";
export type MetaMediaType = "IMAGE" | "VIDEO";

export interface ContentProgramConfig {
  allowedMediaTypes: MetaMediaType[];
  allowedAspectRatios: MetaAspectRatio[];
  allowedImageMimes?: string[];
  allowedVideoMimes?: string[];
  maxImageSizeMb?: number;
  maxVideoSizeMb?: number;
  maxVideoDurationSeconds?: number;
  minResolutionWidth?: number;
  minResolutionHeight?: number;
}

export interface ResolvedContentConstraints {
  maxImageMb: number;
  maxVideoMb: number;
  allowedImageMimes: string[];
  allowedVideoMimes: string[];
  allowedMediaTypes?: MetaMediaType[];
  allowedAspectRatios?: MetaAspectRatio[];
  maxVideoDurationSeconds?: number;
  minResolutionWidth?: number;
  minResolutionHeight?: number;
}

export const META_ASPECT_RATIOS: MetaAspectRatio[] = [
  "1:1",
  "4:5",
  "9:16",
  "16:9",
];

export const ASPECT_RATIO_RECOMMENDED: Record<
  MetaAspectRatio,
  { width: number; height: number; label: string }
> = {
  "9:16": { width: 1080, height: 1920, label: "Stories & Reels" },
  "4:5": { width: 1080, height: 1350, label: "Feed (single media)" },
  "1:1": { width: 1080, height: 1080, label: "Feed & Carousel" },
  "16:9": { width: 1920, height: 1080, label: "In-stream video" },
};

export const DEFAULT_IMAGE_MIMES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];
export const DEFAULT_VIDEO_MIMES = ["video/mp4", "video/quicktime"];

const ASPECT_RATIO_TOLERANCE = 0.02;

function parseCsv(v: string | undefined, fallback: string): string[] {
  return (v ?? fallback)
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export function getContentUploadConstraints(): ResolvedContentConstraints {
  const maxImageMb = Number(import.meta.env.VITE_CONTENT_MAX_IMAGE_MB ?? 10);
  const maxVideoMb = Number(import.meta.env.VITE_CONTENT_MAX_VIDEO_MB ?? 500);
  const allowedImageMimes = parseCsv(
    import.meta.env.VITE_CONTENT_ALLOWED_IMAGE_TYPES,
    DEFAULT_IMAGE_MIMES.join(","),
  );
  const allowedVideoMimes = parseCsv(
    import.meta.env.VITE_CONTENT_ALLOWED_VIDEO_TYPES,
    DEFAULT_VIDEO_MIMES.join(","),
  );
  return { maxImageMb, maxVideoMb, allowedImageMimes, allowedVideoMimes };
}

export function ratioToNumber(ratio: MetaAspectRatio): number {
  const [w, h] = ratio.split(":").map(Number);
  return w / h;
}

export function detectAspectRatio(
  width: number,
  height: number,
): MetaAspectRatio | null {
  if (!width || !height) return null;
  const actual = width / height;
  let best: MetaAspectRatio | null = null;
  let bestDiff = Infinity;
  for (const ratio of META_ASPECT_RATIOS) {
    const diff = Math.abs(actual - ratioToNumber(ratio));
    if (diff < bestDiff) {
      bestDiff = diff;
      best = ratio;
    }
  }
  return bestDiff <= ASPECT_RATIO_TOLERANCE ? best : null;
}

export function aspectRatioToClass(ratio: MetaAspectRatio): string {
  switch (ratio) {
    case "9:16":
      return "aspect-[9/16]";
    case "4:5":
      return "aspect-[4/5]";
    case "1:1":
      return "aspect-square";
    case "16:9":
      return "aspect-video";
    default:
      return "aspect-[9/16]";
  }
}

export function resolveConstraintsFromConfig(
  config?: ContentProgramConfig | null,
): ResolvedContentConstraints {
  const global = getContentUploadConstraints();
  if (!config) return global;
  return {
    maxImageMb: config.maxImageSizeMb ?? global.maxImageMb,
    maxVideoMb: config.maxVideoSizeMb ?? global.maxVideoMb,
    allowedImageMimes:
      config.allowedImageMimes?.map((m) => m.toLowerCase()) ??
      global.allowedImageMimes,
    allowedVideoMimes:
      config.allowedVideoMimes?.map((m) => m.toLowerCase()) ??
      global.allowedVideoMimes,
    allowedMediaTypes: config.allowedMediaTypes,
    allowedAspectRatios: config.allowedAspectRatios,
    maxVideoDurationSeconds: config.maxVideoDurationSeconds,
    minResolutionWidth: config.minResolutionWidth,
    minResolutionHeight: config.minResolutionHeight,
  };
}

export type ValidationCheck = {
  key: string;
  label: string;
  status: "pending" | "pass" | "fail";
  detail?: string;
};

export type ContentValidationResult =
  | {
      ok: true;
      mediaType: MetaMediaType;
      width: number;
      height: number;
      aspectRatio: MetaAspectRatio | null;
      durationSeconds?: number;
      checks: ValidationCheck[];
    }
  | {
      ok: false;
      error: string;
      checks: ValidationCheck[];
      mediaType?: MetaMediaType;
      width?: number;
      height?: number;
      aspectRatio?: MetaAspectRatio | null;
      durationSeconds?: number;
    };

function loadImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions."));
    };
    img.src = url;
  });
}

function loadImageDimensionsFromUrl(
  url: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      reject(new Error("Could not read image dimensions."));
    };
    img.src = url;
  });
}

function loadVideoMetadata(
  file: File,
): Promise<{ width: number; height: number; durationSeconds: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        durationSeconds: video.duration,
      });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video metadata."));
    };
    video.src = url;
  });
}

function loadVideoDimensionsFromUrl(
  url: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      resolve({ width: video.videoWidth, height: video.videoHeight });
    };
    video.onerror = () => {
      reject(new Error("Could not read video metadata."));
    };
    video.src = url;
  });
}

export async function loadMediaDimensionsFromUrl(
  url: string,
  mediaType: MetaMediaType,
): Promise<{ width: number; height: number }> {
  if (mediaType === "VIDEO") {
    return loadVideoDimensionsFromUrl(url);
  }
  return loadImageDimensionsFromUrl(url);
}

export const PREVIEW_RATIO_PRIORITY: MetaAspectRatio[] = [
  "9:16",
  "4:5",
  "16:9",
  "1:1",
];

export type RatioGroupKey = MetaAspectRatio | "unknown";

export const RATIO_GROUP_ORDER: RatioGroupKey[] = [
  ...PREVIEW_RATIO_PRIORITY,
  "unknown",
];

export function getAspectRatioSectionTitle(ratio: RatioGroupKey): string {
  if (ratio === "unknown") return "Other";
  const info = ASPECT_RATIO_RECOMMENDED[ratio];
  return `${info.label} · ${ratio}`;
}

export function pickDefaultPreviewRatio(
  allowed: MetaAspectRatio[] | undefined,
): MetaAspectRatio {
  if (!allowed?.length) return "9:16";
  for (const ratio of PREVIEW_RATIO_PRIORITY) {
    if (allowed.includes(ratio)) return ratio;
  }
  return allowed[0];
}

export function getAspectRatioFromDimensions(
  width: number,
  height: number,
  fallback: MetaAspectRatio,
): MetaAspectRatio {
  return detectAspectRatio(width, height) ?? fallback;
}

export function getAspectClassFromUrlDimensions(
  width: number,
  height: number,
  fallback: MetaAspectRatio,
): string {
  return aspectRatioToClass(getAspectRatioFromDimensions(width, height, fallback));
}

export function getPreviewRatioLabel(
  config: ContentProgramConfig | null | undefined,
  detectedRatio: MetaAspectRatio | null | undefined,
): MetaAspectRatio {
  if (config?.allowedAspectRatios?.length === 1) {
    return config.allowedAspectRatios[0];
  }
  if (detectedRatio) return detectedRatio;
  return pickDefaultPreviewRatio(config?.allowedAspectRatios);
}

export function validateContentFile(
  file: File,
): { ok: true; mediaType: MetaMediaType } | { ok: false; error: string } {
  const mime = (file.type || "").toLowerCase();
  const {
    maxImageMb,
    maxVideoMb,
    allowedImageMimes,
    allowedVideoMimes,
  } = getContentUploadConstraints();

  if (allowedImageMimes.includes(mime)) {
    const maxBytes =
      Number.isFinite(maxImageMb) && maxImageMb > 0
        ? maxImageMb * 1024 * 1024
        : 0;
    if (maxBytes && file.size > maxBytes) {
      return {
        ok: false,
        error: `Image must be at most ${maxImageMb} MB`,
      };
    }
    return { ok: true, mediaType: "IMAGE" };
  }

  if (allowedVideoMimes.includes(mime)) {
    const maxBytes =
      Number.isFinite(maxVideoMb) && maxVideoMb > 0
        ? maxVideoMb * 1024 * 1024
        : 0;
    if (maxBytes && file.size > maxBytes) {
      return {
        ok: false,
        error: `Video must be at most ${maxVideoMb} MB`,
      };
    }
    return { ok: true, mediaType: "VIDEO" };
  }

  return {
    ok: false,
    error:
      "Unsupported file type. Allowed: " +
      [...allowedImageMimes, ...allowedVideoMimes].join(", "),
  };
}

export async function validateContentFileWithConfig(
  file: File,
  config?: ContentProgramConfig | null,
): Promise<ContentValidationResult> {
  const checks: ValidationCheck[] = [];
  const mime = (file.type || "").toLowerCase();
  const resolved = resolveConstraintsFromConfig(config);
  const allowedImageMimes = resolved.allowedImageMimes;
  const allowedVideoMimes = resolved.allowedVideoMimes;

  let mediaType: MetaMediaType | undefined;
  if (allowedImageMimes.includes(mime)) {
    mediaType = "IMAGE";
    checks.push({
      key: "type",
      label: "File type",
      status: "pass",
      detail: mime,
    });
  } else if (allowedVideoMimes.includes(mime)) {
    mediaType = "VIDEO";
    checks.push({
      key: "type",
      label: "File type",
      status: "pass",
      detail: mime,
    });
  } else {
    checks.push({
      key: "type",
      label: "File type",
      status: "fail",
      detail: `Unsupported: ${mime || "unknown"}`,
    });
    return {
      ok: false,
      error: "Unsupported file type for this program.",
      checks,
    };
  }

  if (
    config?.allowedMediaTypes?.length &&
    !config.allowedMediaTypes.includes(mediaType)
  ) {
    checks.push({
      key: "mediaType",
      label: "Media category",
      status: "fail",
      detail: `${mediaType} not allowed`,
    });
    return {
      ok: false,
      error: `${mediaType === "IMAGE" ? "Images" : "Videos"} are not allowed for this program.`,
      checks,
      mediaType,
    };
  }
  checks.push({
    key: "mediaType",
    label: "Media category",
    status: "pass",
    detail: mediaType,
  });

  const maxMb =
    mediaType === "IMAGE" ? resolved.maxImageMb : resolved.maxVideoMb;
  const maxBytes =
    Number.isFinite(maxMb) && maxMb > 0 ? maxMb * 1024 * 1024 : 0;
  if (maxBytes && file.size > maxBytes) {
    checks.push({
      key: "size",
      label: "File size",
      status: "fail",
      detail: `${(file.size / (1024 * 1024)).toFixed(2)} MB (max ${maxMb} MB)`,
    });
    return {
      ok: false,
      error: `File exceeds ${maxMb} MB limit.`,
      checks,
      mediaType,
    };
  }
  checks.push({
    key: "size",
    label: "File size",
    status: "pass",
    detail: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
  });

  let width = 0;
  let height = 0;
  let durationSeconds: number | undefined;

  try {
    if (mediaType === "IMAGE") {
      const dims = await loadImageDimensions(file);
      width = dims.width;
      height = dims.height;
    } else {
      const meta = await loadVideoMetadata(file);
      width = meta.width;
      height = meta.height;
      durationSeconds = meta.durationSeconds;
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Could not read file metadata.";
    checks.push({
      key: "metadata",
      label: "Metadata",
      status: "fail",
      detail: msg,
    });
    return { ok: false, error: msg, checks, mediaType };
  }

  if (resolved.minResolutionWidth && width < resolved.minResolutionWidth) {
    checks.push({
      key: "width",
      label: "Width",
      status: "fail",
      detail: `${width}px (min ${resolved.minResolutionWidth}px)`,
    });
    return {
      ok: false,
      error: `Width must be at least ${resolved.minResolutionWidth}px.`,
      checks,
      mediaType,
      width,
      height,
    };
  }
  if (resolved.minResolutionHeight && height < resolved.minResolutionHeight) {
    checks.push({
      key: "height",
      label: "Height",
      status: "fail",
      detail: `${height}px (min ${resolved.minResolutionHeight}px)`,
    });
    return {
      ok: false,
      error: `Height must be at least ${resolved.minResolutionHeight}px.`,
      checks,
      mediaType,
      width,
      height,
    };
  }
  checks.push({
    key: "resolution",
    label: "Resolution",
    status: "pass",
    detail: `${width}×${height}px`,
  });

  const aspectRatio = detectAspectRatio(width, height);
  if (config?.allowedAspectRatios?.length) {
    if (!aspectRatio) {
      checks.push({
        key: "aspectRatio",
        label: "Aspect ratio",
        status: "fail",
        detail: `${width}×${height} does not match Meta ratios`,
      });
      return {
        ok: false,
        error:
          "Aspect ratio must match 1:1, 4:5, 9:16, or 16:9 (±2% tolerance).",
        checks,
        mediaType,
        width,
        height,
        aspectRatio: null,
        durationSeconds,
      };
    }
    if (!config.allowedAspectRatios.includes(aspectRatio)) {
      checks.push({
        key: "aspectRatio",
        label: "Aspect ratio",
        status: "fail",
        detail: `${aspectRatio} not allowed`,
      });
      return {
        ok: false,
        error: `Aspect ratio ${aspectRatio} is not allowed. Allowed: ${config.allowedAspectRatios.join(", ")}`,
        checks,
        mediaType,
        width,
        height,
        aspectRatio,
        durationSeconds,
      };
    }
    checks.push({
      key: "aspectRatio",
      label: "Aspect ratio",
      status: "pass",
      detail: aspectRatio,
    });
  } else if (aspectRatio) {
    checks.push({
      key: "aspectRatio",
      label: "Aspect ratio",
      status: "pass",
      detail: aspectRatio,
    });
  }

  if (
    mediaType === "VIDEO" &&
    resolved.maxVideoDurationSeconds != null &&
    durationSeconds != null
  ) {
    if (durationSeconds > resolved.maxVideoDurationSeconds) {
      checks.push({
        key: "duration",
        label: "Duration",
        status: "fail",
        detail: `${Math.round(durationSeconds)}s (max ${resolved.maxVideoDurationSeconds}s)`,
      });
      return {
        ok: false,
        error: `Video duration exceeds ${resolved.maxVideoDurationSeconds} seconds.`,
        checks,
        mediaType,
        width,
        height,
        aspectRatio,
        durationSeconds,
      };
    }
    checks.push({
      key: "duration",
      label: "Duration",
      status: "pass",
      detail: `${Math.round(durationSeconds)}s`,
    });
  }

  return {
    ok: true,
    mediaType,
    width,
    height,
    aspectRatio,
    durationSeconds,
    checks,
  };
}

export function getUploadVibe(
  allowedMediaTypes?: MetaMediaType[],
): "image" | "video" | "multimedia" {
  if (!allowedMediaTypes?.length) return "multimedia";
  const hasImage = allowedMediaTypes.includes("IMAGE");
  const hasVideo = allowedMediaTypes.includes("VIDEO");
  if (hasImage && !hasVideo) return "image";
  if (hasVideo && !hasImage) return "video";
  return "multimedia";
}

export function getAcceptMimeList(config?: ContentProgramConfig | null): string {
  const resolved = resolveConstraintsFromConfig(config);
  const types: string[] = [];
  const vibe = getUploadVibe(config?.allowedMediaTypes);
  if (vibe === "image" || vibe === "multimedia") {
    types.push(...resolved.allowedImageMimes);
  }
  if (vibe === "video" || vibe === "multimedia") {
    types.push(...resolved.allowedVideoMimes);
  }
  return types.join(",");
}

export function getPreviewAspectClass(
  config: ContentProgramConfig | null | undefined,
  detectedRatio: MetaAspectRatio | null | undefined,
): string {
  return aspectRatioToClass(getPreviewRatioLabel(config, detectedRatio));
}

export const DEFAULT_CONTENT_PROGRAM_CONFIG: ContentProgramConfig = {
  allowedMediaTypes: ["IMAGE", "VIDEO"],
  allowedAspectRatios: ["9:16"],
  allowedImageMimes: DEFAULT_IMAGE_MIMES,
  allowedVideoMimes: DEFAULT_VIDEO_MIMES,
  maxImageSizeMb: 10,
  maxVideoSizeMb: 500,
  maxVideoDurationSeconds: 90,
};
